// src/contexts/context/SubscriptionContext.js - Versión actualizada
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import agthealthService from '../../services/agthealthService';

const SubscriptionContext = createContext();

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isLoading: true,
    isPro: false,
    hasSubscription: false,
    status: null,
    trialActive: false,
    trialEnd: null,
    currentPeriodEnd: null,
    error: null
  });
  
  // Usar useRef para controlar el estado de las verificaciones
  const checkInProgressRef = useRef(false);
  const lastCheckTimeRef = useRef(0);
  const initialCheckDoneRef = useRef(false);

  // Función para verificar el estado de la suscripción
  const checkSubscriptionStatus = async (forceRefresh = false) => {
    // Evitar verificaciones demasiado frecuentes (mínimo 10 segundos entre verificaciones no forzadas)
    const now = Date.now();
    if (!forceRefresh && now - lastCheckTimeRef.current < 10000) {
      console.log("Verificación de suscripción omitida (demasiado frecuente)");
      return;
    }
    
    // Evitar verificaciones simultáneas
    if (checkInProgressRef.current) {
      console.log("Verificación de suscripción ya en progreso, omitiendo...");
      return;
    }
    
    // Marcar que hay una verificación en progreso
    checkInProgressRef.current = true;
    lastCheckTimeRef.current = now;
    
    try {
      // Verificar si hay token
      const token = localStorage.getItem('agthealth_token');
      if (!token) {
        setSubscriptionStatus(prev => ({
          ...prev,
          isLoading: false,
          isPro: false,
          hasSubscription: false
        }));
        initialCheckDoneRef.current = true;
        return;
      }
      
      // Si no estamos forzando una actualización y ya hemos hecho la verificación inicial,
      // intentar usar información del token primero
      if (!forceRefresh && initialCheckDoneRef.current) {
        try {
          const tokenData = JSON.parse(atob(token.split('.')[1]));
          
          // Si el token tiene información clara sobre la suscripción, usarla
          if (tokenData.isPro === true || tokenData.hasSubscription === true || 
              (tokenData.role === 'independent_professional' && 
               (tokenData.subscription === true || tokenData.subscriptionStatus === 'active'))) {
            
            // No actualizar el estado si ya tenemos datos más completos
            if (subscriptionStatus.status) {
              console.log("Usando datos de suscripción en caché");
              return;
            }
          }
        } catch (e) {
          console.error('Error al decodificar token:', e);
        }
      }
  
      // Consultar la API
      console.log("Verificando estado de suscripción con la API...");
      const response = await agthealthService.getSubscriptionStatus();
  
      if (response.success) {
        console.log("API respondió con estado de suscripción:", response.data);
  
        // Determinar si tiene acceso basado en el estado
        const hasValidSubscription = 
          response.data.trialActive || // Período de prueba activo
          (response.data.hasSubscription && ['active', 'trialing'].includes(response.data.status));
  
        setSubscriptionStatus({
          isLoading: false,
          isPro: hasValidSubscription, // Solo es Pro si tiene suscripción válida
          hasSubscription: response.data.hasSubscription,
          status: response.data.status,
          trialActive: response.data.trialActive,
          trialEnd: response.data.trialEnd,
          currentPeriodEnd: response.data.currentPeriodEnd,
          error: null
        });
        
        initialCheckDoneRef.current = true;
        
        // Disparar un evento personalizado
        window.dispatchEvent(new CustomEvent('subscriptionUpdated', { 
          detail: {...response.data, isPro: hasValidSubscription} 
        }));
      } else {
        throw new Error(response.message || 'Error al verificar estado de suscripción');
      }
    } catch (error) {
      console.error('Error al verificar suscripción:', error);
      setSubscriptionStatus(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Error al verificar suscripción'
      }));
      initialCheckDoneRef.current = true;
    } finally {
      // Desmarcar que hay una verificación en progreso
      checkInProgressRef.current = false;
    }
  };

  // Efecto para verificar la suscripción al montar el componente
  useEffect(() => {
    // Verificar una sola vez al montar
    checkSubscriptionStatus();
    
    // Configurar un intervalo para verificar periódicamente (cada 30 minutos)
    const intervalId = setInterval(() => {
      checkSubscriptionStatus(true);
    }, 30 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Efecto separado para escuchar cambios en el token
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'agthealth_token' || e.key === null) {
        const token = localStorage.getItem('agthealth_token');
        if (token) {
          console.log("Token detectado, verificando suscripción...");
          checkSubscriptionStatus(true);
        } else {
          setSubscriptionStatus(prev => ({
            ...prev,
            isLoading: false,
            isPro: false,
            hasSubscription: false
          }));
        }
      }
    };
    
    // Escuchar cambios en localStorage
    window.addEventListener('storage', handleStorageChange);
    
    // También escuchar evento personalizado
    window.addEventListener('tokenUpdated', () => checkSubscriptionStatus(true));
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tokenUpdated', () => checkSubscriptionStatus(true));
    };
  }, []);

  const value = {
    ...subscriptionStatus,
    refreshStatus: () => checkSubscriptionStatus(true)
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};